import { ComponentPropsWithoutRef, memo, ReactNode } from "react";

import BoxContainer from "@hl/base-components/lib/BoxContainer";
import {
  ClipboardCheck as AllowlistIcon,
  CoinsFill as SpecificCurrencyIcon,
  HexagonFill as SpecificTokenIcon,
  TagFill as SpecificAttributeIcon,
  Grid as AnyTokenIcon,
  FarcasterIcon,
} from "@hl/base-components/lib/assets/icons.generated";
import { SECONDARY_COLOR } from "@hl/base-components/lib/theme/button";
import { STATUS_COLOR, TEXT_COLOR } from "@hl/base-components/lib/theme/colors";
import {
  useFeatureFlags,
  FEATURE_FLAGS,
} from "@hl/shared-features/lib/features/auth/hooks";
import { Stack, Text, Group, Center, Button, Divider } from "@mantine/core";
import { IconCirclePlus } from "@tabler/icons";

import { Condition } from "apollo/graphql.generated";

import { useConditionModals } from "./condition-modals";

export type Props = {
  onAddCondition: (condition: Condition) => void;
  error: string;
  haveMarketingCondition?: boolean;
  isModal?: boolean;
};

const AddConditionBox = ({ onAddCondition, error, isModal }: Props) => {
  const showGateByAttribute = useFeatureFlags("gateByTokenAttribute");
  const showGateByFarcaster = useFeatureFlags(
    FEATURE_FLAGS.GATE_BY_FARCASTER_FOLLOWERS
  );

  const {
    openFindTokenModal,
    openFindCollectionModal,
    openSetCurrencyModal,
    openFindAttributeModal,
    openSetAllowlistModal,
    openFarcasterModal,
  } = useConditionModals(onAddCondition, isModal);

  return (
    <>
      <BoxContainer error={!!error} py={0}>
        <Stack spacing={0}>
          <ConditionButton
            leftIcon={<AllowlistIcon />}
            title={"Is on an allowlist of wallet addresses"}
            subtitle={"Collectors must be on an allowlist of wallet addresses"}
            action={() => openSetAllowlistModal()}
          ></ConditionButton>

          <Divider size="xs" />

          <ConditionButton
            leftIcon={<AnyTokenIcon />}
            title={"Holds any token from a collection"}
            subtitle={
              "Collectors must own any token from a specified collection"
            }
            action={() => openFindCollectionModal()}
          ></ConditionButton>

          <Divider size="xs" />

          <ConditionButton
            leftIcon={<SpecificTokenIcon />}
            title={"Holds a specific token from a collection"}
            subtitle={
              "Collectors must own a specific token from a specified collection"
            }
            action={() => openFindTokenModal()}
          ></ConditionButton>

          <Divider size="xs" />

          {showGateByAttribute && (
            <>
              <ConditionButton
                leftIcon={<SpecificAttributeIcon />}
                title={"Holds a token with a specific trait"}
                subtitle={
                  "Collectors must own any token from a specified collection with a specific trait"
                }
                action={() => openFindAttributeModal()}
              ></ConditionButton>

              <Divider size="xs" />
            </>
          )}

          <ConditionButton
            leftIcon={<SpecificCurrencyIcon />}
            title={"Holds a minimum amount of cryptocurrency or an ERC-20"}
            subtitle={
              "Collectors must hold a minimum amount of ETH, MATIC, or ERC-20"
            }
            action={() => openSetCurrencyModal()}
          ></ConditionButton>

          {showGateByFarcaster && (
            <>
              <Divider size="xs" />
              <ConditionButton
                leftIcon={<FarcasterIcon width={24} height={24} />}
                title={"Farcaster state"}
                subtitle={"Be a Farcaster follower of specific user"}
                action={openFarcasterModal}
              ></ConditionButton>
            </>
          )}
        </Stack>
      </BoxContainer>
      {error && (
        <Text size="xs" mt={5} color={STATUS_COLOR.ERROR}>
          {error}
        </Text>
      )}
    </>
  );
};

export default memo(AddConditionBox);

type ConditionButtonProps = {
  leftIcon: ReactNode;
  title: string;
  subtitle: string;
  action: () => void;
} & ComponentPropsWithoutRef<"div">;

const ConditionButton = ({
  leftIcon,
  title,
  subtitle,
  action,
}: ConditionButtonProps) => {
  return (
    <Group spacing={10} py={12}>
      <Center>{leftIcon}</Center>
      <Stack spacing={0} style={{ flexGrow: 1, flexBasis: 0 }}>
        <Text size="sm">{title}</Text>
        <Text size="xs" color={TEXT_COLOR.SECONDARY}>
          {subtitle}
        </Text>
      </Stack>
      <Button
        color={SECONDARY_COLOR}
        leftIcon={<IconCirclePlus size={16} />}
        size={"sm"}
        onClick={() => action()}
      >
        <Text size="sm">Add condition</Text>
      </Button>
    </Group>
  );
};
